import React, { Fragment, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Button, MenuItem, TextField, Typography } from "@mui/material";
import { FieldArray, Formik } from "formik";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { Modal } from "react-bootstrap";

import API_URL from "../../../redux2";
import { CiSearch } from "react-icons/ci";
import { IoPersonAdd } from "react-icons/io5";
import { ManualEMDSchema } from "../user.util";

const PAYMENT_METHODS = [
  { value: "cbe", label: "CBE" },
  { value: "awash", label: "Awash Bank" },
  { value: "abyssinia", label: "Abyssinia Bank" },
  { value: "amhara", label: "Amhara Bank" },
  { value: "bunna", label: "Bunna Bank" },
  { value: "wegagen", label: "Wegagen Bank" },
  { value: "dashen", label: "Dashen Bank" },
  { value: "zemen", label: "Zemen Bank" },
  { value: "oromia", label: "Oromia Bank" },
  { value: "coop", label: "Cooperative Bank of Oromia" },
  { value: "hibret", label: "Hibret Bank" },
  { value: "wallet", label: "Wallet" },
  { value: "credit", label: "Credit" },
  { value: "short-term-credit", label: "Short-term Credit" },
];

const TYPES = [
  { value: "Visa", label: "Visa" },
  { value: "Baggage", label: "Baggage" },
];

const FORM_FIELDS = [
  {
    label: "Transaction ID",
    name: "transactionId",
    type: "text",
  },
  {
    label: "Base Fare",
    name: "baseFare",
    type: "number",
    readOnly: true,
  },
  {
    label: "Service Charge",
    name: "serviceCharge",
    type: "number",
    readOnly: true,
  },
  {
    label: "Total Amount",
    name: "totalAmount",
    type: "number",
    readOnly: true,
  },
];

const Passenger_Type = [
  { value: "ADT", label: "ADT" },
  { value: "CNN", label: "CNN" },
  { value: "INF", label: "INF" },
  { value: "LBR", label: "LBR" },
];

const PASSENGER_FIELDS = [
  {
    label: "First Name",
    name: "firstName",
    type: "text",
  },
  {
    label: "Last Name",
    name: "lastName",
    type: "text",
  },
  {
    label: "EMD Number",
    name: "ticketNumber",
    type: "text",
  },
  {
    label: "EMD Amount",
    name: "price",
    type: "number",
  },
  {
    label: "Service Charge",
    name: "passengerSC",
    type: "number",
  },
];

const BASE_INITIAL_VALUES = {
  transactionId: "",
  baseFare: 0,
  serviceCharge: 0,
  totalAmount: 0,
  paymentMethod: "",
  typeOfBilling: "EMD",
  typeOfChannel: "",
  flight: {
    origin: "",
    destination: "",
    carrier: "",
    PNR: "",
  },
  tripType: "",
  passengers: [
    {
      firstName: "",
      lastName: "",
      ticketNumber: "",
      price: 0,
      type: "ADT",
      passengerSC: 0,
    },
  ],
};

const FormField = ({
  field,
  values,
  handleChange,
  touched,
  errors,
  margin = true,
  placeholder,
  nestedField,
  setFieldValue,
}) => {
  const fieldName = nestedField ? `${nestedField}.${field.name}` : field.name;
  const fieldValue = nestedField
    ? nestedField === "flight"
      ? values.flight[field.name]
      : values[nestedField.split(".")[0]][nestedField.split(".")[1]]?.[
          field.name
        ]
    : values[field.name];

  const calculateTotals = (passengers) => {
    const baseFare = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.price) || 0),
      0
    );
    const serviceCharge = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.passengerSC) || 0),
      0
    );
    const totalAmount = baseFare + serviceCharge;

    return {
      baseFare: baseFare.toFixed(2),
      serviceCharge: serviceCharge.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const handleFieldChange = (e) => {
    handleChange(e);

    if (
      nestedField &&
      nestedField.startsWith("passengers.") &&
      (field.name === "price" || field.name === "passengerSC")
    ) {
      const updatedPassengers = [...values.passengers];
      const passengerIndex = parseInt(nestedField.split(".")[1]);
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        [field.name]: parseFloat(e.target.value) || 0,
      };

      const totals = calculateTotals(updatedPassengers);

      setFieldValue("baseFare", totals.baseFare);
      setFieldValue("serviceCharge", totals.serviceCharge);
      setFieldValue("totalAmount", totals.totalAmount);
    }
  };

  return (
    <div className={`mb-${margin ? "8" : "2"}`}>
      <TextField
        fullWidth
        id={fieldName}
        type={field.type}
        size="small"
        variant="outlined"
        name={fieldName}
        placeholder={placeholder || field.label}
        label={field.label}
        value={fieldValue || ""}
        onChange={handleFieldChange}
        InputProps={{
          readOnly: field.readOnly || false,
        }}
        error={
          touched?.[nestedField]?.[field.name] && errors?.[nestedField]
            ? errors?.[nestedField]?.[field.name]
              ? errors?.[nestedField]?.[field.name]
              : errors?.[field.name]
            : errors?.[field.name]
        }
        helperText={
          touched?.[nestedField]?.[field.name] && errors?.[nestedField]
            ? errors?.[nestedField]?.[field.name]
              ? errors?.[nestedField]?.[field.name]
              : errors?.[field.name]
            : errors?.[field.name]
        }
        InputLabelProps={{ shrink: true }}
        onWheel={(e) => e.target.blur()}
      />
    </div>
  );
};

const SelectField = ({
  name,
  label,
  value,
  options,
  handleChange,
  touched,
  errors,
}) => (
  <div className="mb-8">
    <TextField
      fullWidth
      select
      id={name}
      name={name}
      size="small"
      value={value}
      label={label}
      onChange={handleChange}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  </div>
);

const AddManualEMDModal = ({
  openManualEMD,
  setOpenManualEMD,
  handleManualEMD,
}) => {
  const handleSubmit = (values, { setSubmitting }) => {
    handleManualEMD(values);
    setSubmitting(false);
  };

  const handleClose = () => setOpenManualEMD(false);

  const [initialValues, setInitialValues] = useState(BASE_INITIAL_VALUES);

  const calculateTotals = (passengers) => {
    const baseFare = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.price) || 0),
      0
    );
    const serviceCharge = passengers.reduce(
      (sum, passenger) => sum + (parseFloat(passenger.passengerSC) || 0),
      0
    );
    const totalAmount = baseFare + serviceCharge;

    return {
      baseFare: baseFare.toFixed(2),
      serviceCharge: serviceCharge.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  return (
    <div>
      <div className="cursor-pointer" onClick={() => setOpenManualEMD(true)}>
        Add Manual EMD
      </div>

      <Modal
        className="modal fade"
        show={openManualEMD}
        onHide={handleClose}
        size="lg"
      >
        <div className="" role="document">
          <div className="">
            <div className="modal-header">
              <h4 className="modal-title fs-20">Add Manual EMD</h4>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                data-dismiss="modal"
              />
            </div>

            <div className="modal-body">
              <i className="text-black flat-icon-cancel-12 close" />
              <div className="add-contact-box">
                <Formik
                  initialValues={initialValues}
                  validationSchema={ManualEMDSchema}
                  enableReinitialize
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    errors,
                    touched,
                    setFieldValue,
                  }) => {
                    return (
                      <form onSubmit={handleSubmit}>
                        <div className="flex flex-col justify-center">
                          <div className="row">
                            {/* Passengers */}
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Passengers
                            </Typography>
                            <FieldArray
                              name="passengers"
                              className="flex flex-col gap-6"
                            >
                              {({ push, remove }) => (
                                <Fragment>
                                  {values.passengers.map((passenger, index) => (
                                    <div
                                      key={index}
                                      className="flex flex-col gap-4 items-center p-2 md:flex-row"
                                    >
                                      {PASSENGER_FIELDS.map((field) => (
                                        <FormField
                                          key={`${field.name}-${index}`}
                                          field={field}
                                          values={values}
                                          handleChange={handleChange}
                                          touched={touched}
                                          errors={errors}
                                          nestedField={`passengers.${index}`}
                                          margin={false}
                                          setFieldValue={setFieldValue}
                                        />
                                      ))}
                                      <div className="mb-2">
                                        <TextField
                                          fullWidth
                                          select
                                          id={`passengers.${index}.type`}
                                          name={`passengers.${index}.type`}
                                          size="small"
                                          value={passenger.type || "ADT"}
                                          label="Passenger Type"
                                          onChange={handleChange}
                                        >
                                          {Passenger_Type.map((option) => (
                                            <MenuItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </div>
                                      {values.passengers.length > 1 && (
                                        <FaTrash
                                          className="w-4 h-4 text-red-500 transition-colors duration-200 cursor-pointer hover:text-red-900"
                                          onClick={() => {
                                            remove(index);
                                            const totals = calculateTotals(
                                              values.passengers
                                            );
                                            setFieldValue(
                                              "baseFare",
                                              totals.baseFare -
                                                parseFloat(
                                                  values.passengers[index].price
                                                )
                                            );
                                            setFieldValue(
                                              "serviceCharge",
                                              totals.serviceCharge -
                                                parseFloat(
                                                  values.passengers[index]
                                                    .passengerSC
                                                )
                                            );
                                            setFieldValue(
                                              "totalAmount",
                                              totals.totalAmount -
                                                parseFloat(
                                                  values.passengers[index].price
                                                ) -
                                                parseFloat(
                                                  values.passengers[index]
                                                    .passengerSC
                                                )
                                            );
                                          }}
                                          type="button"
                                        />
                                      )}
                                    </div>
                                  ))}
                                  <div className="flex justify-center items-center mt-2 mb-4">
                                    {/* <button
                                      className="px-4 py-2 text-black rounded-md border-main border-1"
                                      onClick={() =>
                                        push({
                                          firstName: "",
                                          lastName: "",
                                          ticketNumber: "",
                                          price: 0,
                                          type: "ADT",
                                          passengerSC: 0,
                                        })
                                      }
                                      type="button"
                                    >
                                      Add Passenger
                                    </button> */}
                                    <div className="flex gap-2 items-center px-4 py-2 rounded-md border">
                                      <IoPersonAdd
                                        size={24}
                                        className="text-green-500 transition-colors duration-200 cursor-pointer hover:text-green-900"
                                        onClick={() =>
                                          push({
                                            firstName: "",
                                            lastName: "",
                                            ticketNumber: "",
                                            price: 0,
                                            type: "ADT",
                                            passengerSC: 0,
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                </Fragment>
                              )}
                            </FieldArray>

                            <Typography variant="h6" sx={{ mb: 2 }}>
                              Billing Details
                            </Typography>
                            <div className="flex flex-row gap-2 items-center">
                              {FORM_FIELDS.map((field) => (
                                <FormField
                                  name={field.name}
                                  label={field.label}
                                  key={field.name}
                                  field={field}
                                  values={values}
                                  handleChange={handleChange}
                                  touched={touched}
                                  errors={errors}
                                  setFieldValue={setFieldValue}
                                />
                              ))}
                            </div>

                            {/* Payment Method */}
                            <SelectField
                              name="paymentMethod"
                              label="Payment Method"
                              value={values.paymentMethod}
                              options={PAYMENT_METHODS}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                            />

                            <SelectField
                              name="typeOfChannel"
                              label="Type"
                              value={values.typeOfChannel}
                              options={TYPES}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                            />
                          </div>

                          <div className="flex gap-4 justify-center items-center w-full">
                            <Button
                              onClick={handleClose}
                              variant="danger"
                              fullWidth
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              className="mt-8"
                              variant="contained"
                              type="submit"
                              loading={isSubmitting}
                              disabled={isSubmitting}
                              fullWidth
                            >
                              Add EMD
                            </LoadingButton>
                          </div>
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddManualEMDModal;
